import { useEffect, useMemo, useState } from "react";
import { ConfirmDialog, DiamondCheckbox, SearchField, TextField } from "@legends/ui";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import DataGrid from "./DataGrid";
import AdminModal from "./admin/AdminModal";
import {
  archiveCave,
  createCave,
  deleteCave,
  fetchConfigurableCaves,
  previewCaveHost,
  unarchiveCave,
  type Cave,
  type CaveHostPreview,
  type CreatedCave,
} from "./api";
import { alertStyle, errMsg, monoLabelStyle } from "./sections";

// Super-admin cave provisioning (ADR 0023), on the Cave-themed AG Grid (ADR 0156).
// Create a cave from the pinned top row (name + optional slug + optional headline
// legend) — the tenant (the internal authz domain) is auto-provisioned 1:1 and the
// host's DNS record is created, so the cave is live immediately. Archive/unarchive is
// an inline "Archived" checkbox (a reversible toggle, ADR 0067); delete is the grid's
// inline ✕, which opens a destructive confirm modal that surfaces the 409
// "has activity — archive instead" reason in place (ADR 0059).

// The grid row is a cave plus two create-only fields the pinned add-row captures:
// `slug` (shown as the Host column on the add-row — it derives the host) and the
// optional headline `legendId`. Both are blank on existing rows.
type Row = Cave & { slug?: string; legendId?: string };

// PublicAddress renders the fan-facing address a cave name would produce, in plain
// language (LSC-328) — no slug/DNS/host jargon. Green + reassuring when the address
// is free; amber with a plain reason when it's taken or invalid.
function PublicAddress({ preview, previewing }: { preview: CaveHostPreview | null; previewing: boolean }) {
  if (!preview && !previewing) return null;
  const ok = preview?.available === true && Boolean(preview.host);
  const edge = ok ? "var(--accent, #c9a227)" : "var(--live, #e8735a)";
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        border: `1px solid ${ok ? "var(--border-strong, #3a352b)" : edge + "88"}`,
        background: "var(--surface-1, #17150f)",
        borderRadius: "var(--radius-md, 8px)",
        padding: "10px 12px",
      }}
    >
      <span style={{ display: "block", ...monoLabelStyle, color: "var(--text-label)", marginBottom: 4 }}>Public address</span>
      {previewing && !preview ? (
        <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-dim)" }}>Checking…</span>
      ) : (
        <>
          {preview?.host && (
            <span style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: "var(--fs-body-2)", color: ok ? "var(--accent-hi, #e6c65a)" : "var(--text-2)", wordBreak: "break-all" }}>
              {preview.host}
            </span>
          )}
          <span style={{ display: "block", marginTop: 4, fontSize: "var(--fs-xs)", color: ok ? "var(--text-dim)" : edge }}>
            {ok ? "Fans will see and share this link." : preview?.reason ?? "Enter a cave name to see its public address."}
          </span>
        </>
      )}
    </div>
  );
}

export default function CavesAdminScreen({ onOpenCave }: { onOpenCave?: (cave: Cave) => void } = {}) {
  const [caves, setCaves] = useState<Cave[]>([]);
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedCave | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  // Delete confirmation (ADR 0059): the cave pending deletion drives the modal.
  const [pendingDelete, setPendingDelete] = useState<Cave | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Create modal (ADR 0160, Phase 4): the "＋ Add a cave" button opens it — the
  // create fields (name + optional slug + optional headline legend) don't map 1:1
  // to the grid columns (host is derived, tenant is auto), so it's a dedicated form.
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newLegend, setNewLegend] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const resetAdd = () => { setNewName(""); setNewSlug(""); setNewLegend(""); setPreview(null); };
  // Live "public address" preview (LSC-328): show the fan-facing address a name
  // would produce — in plain language — before the operator commits, and whether
  // it's free. Server-derived (debounced) so it never disagrees with the create.
  const [preview, setPreview] = useState<CaveHostPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  useEffect(() => {
    const name = newName.trim();
    if (!name) { setPreview(null); setPreviewing(false); return; }
    setPreviewing(true);
    const t = setTimeout(() => {
      previewCaveHost(name, newSlug.trim())
        .then(setPreview)
        .catch(() => setPreview(null))
        .finally(() => setPreviewing(false));
    }, 350);
    return () => clearTimeout(t);
  }, [newName, newSlug]);

  const load = (withArchived = showArchived) =>
    fetchConfigurableCaves(withArchived).then(setCaves).catch((e: unknown) => setError(errMsg(e)));
  useEffect(() => void load(showArchived), [showArchived]);

  const columns = useMemo<ColDef<Row>[]>(
    () => [
      {
        // The cave name opens the cave object page (Phase 2). Only the pinned add-row
        // is editable (to enter the new cave's name); existing rows are links.
        field: "name",
        headerName: "Cave",
        minWidth: 160,
        editable: (p) => p.node?.rowPinned === "top",
        cellRenderer: (p: ICellRendererParams<Row>) => {
          if (p.node?.rowPinned === "top") return p.value ?? "";
          const cave = p.data;
          if (!cave || !onOpenCave) return p.value ?? "";
          return (
            <button
              type="button"
              onClick={() => onOpenCave(cave)}
              style={{ background: "none", border: "none", padding: 0, color: "var(--text-1)", textDecoration: "underline", cursor: "pointer", font: "inherit" }}
            >
              {cave.name}
            </button>
          );
        },
      },
      {
        // Data rows show the live host; the add-row edits the slug that derives it.
        field: "host",
        headerName: "Host",
        minWidth: 170,
        editable: false,
        valueGetter: (p) => (p.node?.rowPinned === "top" ? p.data?.slug ?? "" : p.data?.host ?? ""),
        valueSetter: (p) => {
          if (p.node?.rowPinned === "top" && p.data) {
            p.data.slug = String(p.newValue ?? "");
            return true;
          }
          return false;
        },
        cellStyle: () => ({ fontFamily: "var(--font-mono)", color: "var(--gold)" }),
      },
      { field: "tenant", headerName: "Tenant", minWidth: 120, editable: false, cellStyle: () => ({ color: "var(--text-muted)" }) },
      {
        // Create-only: the optional headline legend id, entered only on the add-row.
        field: "legendId",
        headerName: "Headline legend",
        minWidth: 150,
        editable: false,
        filter: false,
        sortable: false,
        valueGetter: (p) => (p.node?.rowPinned === "top" ? p.data?.legendId ?? "" : ""),
      },
      {
        // Inline archive toggle (ADR 0067): check to retire, uncheck to restore.
        field: "archived",
        headerName: "Archived",
        width: 120,
        maxWidth: 130,
        editable: true,
        filter: false,
        cellEditor: "agCheckboxCellEditor",
        cellRenderer: "agCheckboxCellRenderer",
      },
    ],
    [],
  );

  const onEdit = async (row: Row, field: string, value: unknown) => {
    if (field !== "archived") return;
    setError(null);
    try {
      if (value) await archiveCave(row.id);
      else await unarchiveCave(row.id);
    } catch (e) {
      // Surface the reason and let the reload below revert the optimistic toggle.
      setError(errMsg(e));
    } finally {
      await load();
    }
  };

  // submitCreate returns true to close the modal, false to keep it open (a
  // validation error or a failed request — the operator can fix + retry).
  const submitCreate = async (): Promise<boolean> => {
    if (!newName.trim()) {
      setError("Cave name is required.");
      return false;
    }
    setError(null);
    setCreated(null);
    setAddBusy(true);
    try {
      const res = await createCave({
        name: newName.trim(),
        slug: newSlug.trim() || undefined,
        legendId: newLegend.trim() || undefined,
      });
      setCreated(res);
      resetAdd();
      await load();
      return true;
    } catch (e) {
      setError(errMsg(e));
      return false;
    } finally {
      setAddBusy(false);
    }
  };

  const askDelete = (cave: Cave) => {
    setDeleteError(null);
    setPendingDelete(cave);
  };
  const cancelDelete = () => {
    if (deleteBusy) return;
    setPendingDelete(null);
    setDeleteError(null);
  };
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteCave(pendingDelete.id);
      setPendingDelete(null);
      await load();
    } catch (e: unknown) {
      // Surface the server reason (e.g. a 409 "archive it instead") inside the
      // modal so the operator sees why, and keep it open.
      setDeleteError(errMsg(e));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div>
      <h1 style={{ margin: 0, ...monoLabelStyle, fontWeight: 600 }}>Caves</h1>
      <p style={{ margin: "10px 0 14px", fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
        Create a cave with <strong>＋ Add a cave</strong> — its tenant is provisioned automatically and its host DNS
        record is created, so it's live immediately. A pristine cave (nothing sold, nothing run) can be{" "}
        <strong>deleted</strong> with the ✕;
        once it has activity it must be <strong>archived</strong> (check <em>Archived</em>) — retired from the storefront
        and console while its history is kept. Archiving unlinks its headlining Legend: if that was the Legend&rsquo;s
        only active cave, the Legend stops appearing publicly/featured until another cave is linked (the Legend record
        and any other cave links are untouched).
      </p>

      {error && (
        <p role="alert" style={alertStyle}>
          {error}
        </p>
      )}
      {created && (
        <div
          role="status"
          style={{
            margin: "0 0 14px",
            padding: "10px 14px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-strong)",
            background: "var(--accent-tint)",
            color: "var(--accent-hi)",
            fontSize: "var(--fs-body-2)",
          }}
        >
          Created <strong>{created.cave.name}</strong> at{" "}
          <span style={{ fontFamily: "var(--font-mono)" }}>{created.host}</span> — DNS{" "}
          {created.dnsStatus === "created"
            ? "record created"
            : created.dnsStatus === "skipped"
              ? "not managed here"
              : "provisioning failed (retry from your DNS console)"}
          .
        </div>
      )}

      <div style={{ margin: "0 0 10px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <SearchField value={q} onChange={setQ} placeholder="Filter caves…" width={320} />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", ...monoLabelStyle, color: "var(--text-dim)" }}>
          <DiamondCheckbox checked={showArchived} onChange={() => setShowArchived((v) => !v)} aria-label="Show archived caves" />
          Show archived
        </label>
        <span style={{ ...monoLabelStyle, marginLeft: "auto" }}>{caves.length} caves</span>
      </div>

      <DataGrid<Row>
        rows={caves}
        columns={columns}
        getRowId={(r) => r.id}
        onEdit={onEdit}
        onDelete={askDelete}
        addModal
        addLabel="a cave"
        paginated
        renderAddModal={({ close }) => (
          <AdminModal
            title="Add a cave"
            confirmLabel={addBusy ? "Creating…" : "Create cave"}
            busy={addBusy}
            confirmDisabled={previewing || (preview != null && !preview.available)}
            onConfirm={() => void submitCreate().then((ok) => { if (ok) close(); })}
            onCancel={() => { if (!addBusy) { resetAdd(); setError(null); close(); } }}
          >
            <p style={{ margin: "0 0 12px", fontSize: "var(--fs-sm)", color: "var(--text-2)" }}>
              Give the cave a name. It goes live right away with its own public link that fans can visit and share.
            </p>
            <div style={{ display: "grid", gap: 12, minWidth: 300 }}>
              <TextField label="Cave name" name="cave-name" value={newName} onChange={setNewName} placeholder="e.g. Acme Cavern" />
              <div>
                <TextField label="Web address (optional)" name="cave-slug" value={newSlug} onChange={setNewSlug} placeholder="acme" />
                <span style={{ display: "block", marginTop: 4, fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
                  Short and simple — leave blank to use the cave name. This becomes the permanent public link.
                </span>
              </div>
              <TextField label="Headline legend id (optional)" name="cave-legend" value={newLegend} onChange={setNewLegend} placeholder="legend id" />
              <PublicAddress preview={preview} previewing={previewing} />
              {error && <span role="alert" style={{ fontSize: "var(--fs-sm)", color: "var(--live, #e8735a)" }}>{error}</span>}
            </div>
          </AdminModal>
        )}
        quickFilterText={q}
        label="Caves"
        emptyText="No caves yet — add one with ＋ Add a cave."
      />

      {pendingDelete && (
        <ConfirmDialog
          title={`Delete ${pendingDelete.name}?`}
          destructive
          confirmLabel="Delete cave"
          busy={deleteBusy}
          error={deleteError ?? undefined}
          onConfirm={() => void confirmDelete()}
          onCancel={cancelDelete}
        >
          This permanently removes <strong>{pendingDelete.name}</strong> (
          <span style={{ fontFamily: "var(--font-mono)" }}>{pendingDelete.host}</span>) and all of its events, tickets,
          broadcasts, recordings, and DNS record. This cannot be undone. If the cave has sold a ticket or run an event,
          delete is refused — archive it instead.
        </ConfirmDialog>
      )}
    </div>
  );
}
